import { Inject, Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, switchMap, mergeMap, first, filter } from 'rxjs';
import { Observable, forkJoin, of } from 'rxjs';
import {
  ProductPrice,
  CustomerProductPrice,
  BackendPrice,
  BackendPriceLevel,
  CartRowPrice,
  Pricelist
} from '../../models/price';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';
import { ProductService } from '../products/product.service';
import { Customer } from '../../models';
import { GungStringConverterService } from '../gung-string-converter.service';
import { CartRow } from '../../state/cart/types';
import { GungFlowService } from '../gung-flow/gung-flow.service';
import { gungComparatorHelper } from '../../utils/gung-utils';
import { NavigationConfig } from '../../models/navigation-config';
import { CustomerService } from '../customers/customer.service';

export class ProductPriceContext {
  serialVersionUID: number;
  productId: string;
  quantity: number;
  customerId: string;
  flowId: string;
  priceListId: string;
}

@Injectable({
  providedIn: 'root'
})
export class PriceService {
  protected cachedCustomerPrices: { [productId: string]: BackendPrice } = {};
  public qtyGroupingLevel: 'MODEL' | 'VARIANT' | 'SKU';
  public defaultCurrency: string = 'SEK';
  /**
   * AI order parser pricing, for a customer that may not be the selected one.
   * false (default): standard ERP price via fetchCustomerPricesUncached, no cache, no customer-project logic.
   * true: getCustomParserPrices is used instead - a customer project sets this and overrides
   * getCustomParserPrices with its own price logic, which must not read or write cachedCustomerPrices.
   */
  public parserUsesCustomPricing = false;
  /** For resolving customers other than the selected one (parser pricing). */
  protected customerLookupService = inject(CustomerService);

  constructor(
    protected http: HttpClient,
    protected customerService: SelectedCustomerService,
    protected productService: ProductService,
    protected gungFlowService: GungFlowService,
    @Inject('environment')
    protected environment: NavigationConfig
  ) {
    customerService.getSelectedCustomer().subscribe(() => (this.cachedCustomerPrices = {}));

    gungFlowService.getSelectedFlow().subscribe(() => (this.cachedCustomerPrices = {}));
  }

  getCurrentCustomerPrice(productId: string): Observable<CustomerProductPrice> {
    return this.customerService
      .getSelectedCustomer()
      .pipe(switchMap(cust => this.getCustomerPrice(productId, cust.id)));
  }

  getCurrentCustomerPrices(productIds: string[]): Observable<CustomerProductPrice[]> {
    return this.customerService
      .getSelectedCustomer()
      .pipe(switchMap(cust => this.getCustomerPrices(productIds, cust.id)));
  }

  getCurrentCustomerPriceWithQty(input: { [productId: string]: number }): Observable<CustomerProductPrice[]> {
    return this.customerService.getSelectedCustomer().pipe(
      filter(cust => !!cust),
      switchMap(cust => this.getCustomerPricesWithQtys(input, cust.id))
    );
  }

  getPrice(productId: string): Observable<ProductPrice> {
    // TODO
    return null;
  }

  getCustomerPrice(productId: string, customerId: string): Observable<CustomerProductPrice> {
    return this.internalHttpGetCustomerPrice(productId, customerId).pipe(
      mergeMap(x => forkJoin([of(x), this.customerService.getSelectedCustomer().pipe(first())])),
      map(([backendPrice, customer]) => {
        return this.mapCustomerProductPrice(backendPrice, customer);
      })
    );
  }

  getCustomerPrices(productIds: string[], customerId: string): Observable<CustomerProductPrice[]> {
    return this.internalHttpGetCustomerPrices(productIds, customerId).pipe(
      switchMap(x => forkJoin([of(x), this.customerService.getSelectedCustomer().pipe(first())])),
      map(([backendPrices, customer]) => {
        if (!customer) return [];
        return Object.values(backendPrices)
          .filter(bp => !!bp)
          .map(backendPrice => this.mapCustomerProductPrice(backendPrice, customer));
      })
    );
  }

  getCustomerPricesWithQtys(
    input: { [productId: string]: number },
    customerId: string
  ): Observable<CustomerProductPrice[]> {
    return this.internalHttpGetCustomerPrices(Object.keys(input), customerId, input).pipe(
      switchMap(resp =>
        forkJoin({
          prices: of(resp),
          customer: this.customerService.getSelectedCustomer().pipe(first())
        })
      ),
      map(({ prices, customer }) => {
        if (!customer) return [];
        return Object.values(prices)
          .filter(price => !!price)
          .map(price => this.mapCustomerProductPrice(price, customer, input[price.productId]));
      })
    );
  }

  getCustomerPricesForParser(input: { [productId: string]: number }, customerId: string): Observable<CustomerProductPrice[]> {
    return this.parserUsesCustomPricing
      ? this.getCustomParserPrices(input, customerId)
      : this.getStandardParserPrices(input, customerId);
  }

  /** Override together with parserUsesCustomPricing = true; the default is the standard parser path. */
  protected getCustomParserPrices(input: { [productId: string]: number }, customerId: string): Observable<CustomerProductPrice[]> {
    return this.getStandardParserPrices(input, customerId);
  }

  protected getStandardParserPrices(input: { [productId: string]: number }, customerId: string): Observable<CustomerProductPrice[]> {
    return this.fetchCustomerPricesUncached(Object.keys(input), customerId, input).pipe(
      switchMap(resp =>
        forkJoin({
          prices: of(resp),
          customer: this.customerLookupService.getCustomer(customerId).pipe(first())
        })
      ),
      map(({ prices, customer }) => {
        if (!customer) return [];
        return Object.values(prices)
          .filter(price => !!price)
          .map(price => this.mapCustomerProductPrice(price, customer, input[price.productId]));
      })
    );
  }

  public getPriceLevel(backendPrice: BackendPrice, qty?: number): BackendPriceLevel {
    if (!backendPrice?.levels?.length) return null;
    let level = backendPrice.levels[0];

    if (!qty) {
      return level;
    }
    // Don't modify the array!
    const allSortedLevels = [...backendPrice.levels].sort((a, b) => gungComparatorHelper(a.quantity, b.quantity, 1));
    // loop till I am at the level which is right below too much
    for (let i = 0; i < allSortedLevels.length; i++) {
      const thisLevel = allSortedLevels[i];
      if (thisLevel.quantity > qty) {
        break;
      }
      level = thisLevel;
    }
    if (level && level.discount) {
      level.discount = Math.abs(level.discount);
    }
    return level;
  }

  protected mapCustomerProductPrice(backendPrice: BackendPrice, customer: Customer, qty?: number) {
    const priceLevel = this.getPriceLevel(backendPrice, qty);

    // TODO - rename? not necessarily customerDiscount, can be staffed price discount
    let customerDiscountPercent = this.getCustomerDiscountPercent(priceLevel, backendPrice);

    let customerGrossPrice = priceLevel.price / ((100 - customerDiscountPercent) / 100);
    let customerNetPrice = priceLevel.price;
    const currencyCode = backendPrice.currencyCode || customer.extra?.gme?.currencyCode || this.defaultCurrency;

    if (this.environment.standardErp) {
      if (this.getCustomerDiscountPercent(priceLevel, backendPrice) === 0) {
        customerDiscountPercent = 0;
      } else {
        customerDiscountPercent = this.getCustomerDiscountPercent(priceLevel, backendPrice) || backendPrice.lineDiscountPercent || 0;
      }
      const productPrice = priceLevel?.price || backendPrice.wsp;
      customerGrossPrice = backendPrice.extra && '_customerGrossPrice' in backendPrice.extra && backendPrice.extra._customerGrossPrice > 0 ?
        backendPrice.extra._customerGrossPrice :
        productPrice;
      customerNetPrice = (productPrice * (100 - customerDiscountPercent)) / 100;
    }

    const customerProductPrice: CustomerProductPrice = {
      productId: backendPrice.productId,
      customerId: customer.id,
      customerDiscountPercent,
      customerGrossPrice: {
        value: customerGrossPrice,
        currencyCode
      },
      customerNetPrice: {
        value: customerNetPrice,
        currencyCode
      },
      customerDiscountAmount: {
        value: customerGrossPrice - customerNetPrice,
        currencyCode
      },
      backendPriceLevel: priceLevel,
      priceFactor: backendPrice.priceFactor,
      backendPrice: backendPrice,
      recommendedRetailPrice: {
        value: backendPrice.rrp,
        currencyCode: backendPrice.currencyCode
      }
    };

    return customerProductPrice;
  }

  getCustomerDiscountPercent(priceLevel: BackendPriceLevel, backendPrice: BackendPrice) {
    if (this.environment.standardErp && priceLevel.extra && '_allowLineDisc' in priceLevel.extra && !priceLevel.extra._allowLineDisc) {
      return 0;
    }
    return priceLevel.discount;
  }

  protected internalHttpGetCustomerPrice(productId: string, customerId: string): Observable<BackendPrice> {
    const convertedId = GungStringConverterService.toGungString(productId);

    return this.gungFlowService.getSelectedFlow().pipe(
      first(),
      switchMap(selectedFlow =>
        this.http.get<BackendPrice>(`json/product-price/${GungStringConverterService.toGungString(customerId)}/${convertedId}?flowId=${selectedFlow.id}`, {
          headers: { maxAge: '86400' }
        })
      )
    );
  }

  protected internalHttpGetCustomerPrices(
    productIds: string[],
    customerId: string,
    productQty?: { [productId: string]: number }
  ): Observable<{ [productId: string]: BackendPrice }> {
    const newListProductsIds = productIds.filter(productId => !this.cachedCustomerPrices[productId]);

    return this.gungFlowService.getSelectedFlow().pipe(
      first(),
      switchMap(selectedFlow => {
        if (newListProductsIds.length > 0) {
          const productPriceContext: ProductPriceContext[] = [];
          for (const productId of newListProductsIds) {
            productPriceContext.push({
              serialVersionUID: 0,
              productId: productId,
              quantity: productQty && productQty[productId] ? productQty[productId] : 1,
              customerId: customerId,
              flowId: selectedFlow.id,
              priceListId: ''
            });
          }
          return this.http.post<{ [productId: string]: BackendPrice }>(
            `json/product-price/context`,
            productPriceContext
          );
        }
        return of(null);
      }),
      map((customerPrices: { [productId: string]: BackendPrice }) => {
        // in case any data from backend
        // then store it in cache
        if (!!customerPrices) {
          for (const customerPriceKey of Object.keys(customerPrices)) {
            this.cachedCustomerPrices[customerPriceKey] = customerPrices[customerPriceKey];
          }
        }

        // then return the data for requested productIds
        const toReturn: { [productId: string]: BackendPrice } = {};
        for (const productId of productIds) {
          toReturn[productId] = this.cachedCustomerPrices[productId];
        }
        return toReturn;
      })
    );
  }

  /** Same request as internalHttpGetCustomerPrices, without reading or writing cachedCustomerPrices. */
  protected fetchCustomerPricesUncached(
    productIds: string[],
    customerId: string,
    productQty?: { [productId: string]: number }
  ): Observable<{ [productId: string]: BackendPrice }> {
    if (!productIds.length) return of({});
    return this.gungFlowService.getSelectedFlow().pipe(
      first(),
      switchMap(selectedFlow =>
        this.http.post<{ [productId: string]: BackendPrice }>(
          `json/product-price/context`,
          productIds.map(productId => ({
            serialVersionUID: 0,
            productId: productId,
            quantity: productQty && productQty[productId] ? productQty[productId] : 1,
            customerId: customerId,
            flowId: selectedFlow.id,
            priceListId: ''
          }))
        )
      ),
      map(customerPrices => customerPrices || {})
    );
  }

  public getCartRowPriceObservable(cartRow: CartRow): Observable<CartRowPrice> {
    return this.getCurrentCustomerPriceWithQty({ [cartRow.productId]: cartRow.qty }).pipe(
      first(),
      map(price => {
        return this.getCartRowPrice(price[0], cartRow);
      })
    );
  }

  public getCartRowPricesObservable(cartRows: CartRow[]): Observable<CartRowPrice[]> {
    const productIdQuantityMap: { [productId: string]: number } = {};
    const productIdPromises: Observable<string>[] = [];
    cartRows.forEach(cartRow => {
      switch (this.qtyGroupingLevel) {
        case 'MODEL': {
          productIdPromises.push(this.productService.getModelId(cartRow.productId));
          break;
        }
        case 'VARIANT': {
          productIdPromises.push(this.productService.getVariantProductId(cartRow.productId));
          break;
        }
        default: {
          productIdQuantityMap[cartRow.productId] = cartRow.qty;
          break;
        }
      }
    });

    if (productIdPromises.length) {
      return forkJoin(productIdPromises).pipe(
        switchMap(productIds => {
          this.fillGroupedProductIdQuantityMap(
            cartRows,
            productIdQuantityMap,
            Object.assign({}, ...cartRows.map((cartRow, index) => ({ [cartRow.productId]: productIds[index] })))
          );
          return this.getCartRowsPricesFromProductIdQuantityMap(productIdQuantityMap, cartRows);
        })
      );
    } else {
      return this.getCartRowsPricesFromProductIdQuantityMap(productIdQuantityMap, cartRows);
    }
  }

  protected getCartRowsPricesFromProductIdQuantityMap(
    productIdQuantityMap: { [productId: string]: number },
    cartRows: CartRow[]
  ): Observable<CartRowPrice[]> {
    return this.getCurrentCustomerPriceWithQty(productIdQuantityMap).pipe(
      first(),
      map(prices => {
        return this.getCartRowPrices(prices, cartRows);
      })
    );
  }

  protected fillGroupedProductIdQuantityMap(
    cartRows: CartRow[],
    productIdQuantityMap: { [productId: string]: number },
    productIdsMapped
  ) {
    const groupedQuantities = {};
    cartRows.forEach(cartRow => {
      let groupedId = productIdsMapped[cartRow.productId];
      groupedQuantities[groupedId] = groupedQuantities[groupedId] ?? 0;
      groupedQuantities[groupedId] += cartRow.qty;
    });
    cartRows.forEach(cartRow => {
      productIdQuantityMap[cartRow.productId] = groupedQuantities[productIdsMapped[cartRow.productId]];
    });
  }

  public getCartRowPrices(customerPrices: CustomerProductPrice[], cartRows: CartRow[]): CartRowPrice[] {
    return cartRows.map(cartRow => {
      const customerPrice = customerPrices.find(price => price.productId === cartRow.productId);
      return this.getCartRowPrice(customerPrice, cartRow);
    });
  }

  public getCartRowPrice(customerPrice: CustomerProductPrice, cartRow: CartRow): CartRowPrice {
    const baseCurrencyCode = customerPrice.customerNetPrice.currencyCode;

    const customPrice = Number(cartRow.extra.gme && cartRow.extra.gme.unitNetPrice);
    const customDiscount = Number(cartRow.extra.gme && cartRow.extra.gme.discountPercentage);

    let price: number;
    let priceAfterDiscount: number;
    let percentDiscount: number;
    let valueDiscount: number;
    price = customerPrice.customerGrossPrice.value;
    if (!isNaN(customPrice) || !isNaN(customDiscount)) {
      percentDiscount = customerPrice.customerDiscountPercent;

      if (!isNaN(customPrice)) {
        price = customPrice;
      }


      if (!isNaN(customDiscount)) {
        percentDiscount = customDiscount;
      }

      price = Number(Number(price).toFixed(2));
      priceAfterDiscount = price * ((100 - percentDiscount) / 100);
      valueDiscount = price - priceAfterDiscount;
    }

    const res: CartRowPrice = {
      ...customerPrice,
      productPartialId: cartRow.productPartialId,
      cartRowUnitPrice: { value: price, currencyCode: baseCurrencyCode },
      cartRowUnitPriceInclRowDiscount: {
        value: priceAfterDiscount ?? customerPrice.customerNetPrice.value,
        currencyCode: baseCurrencyCode
      },
      quantity: cartRow.qty,
      cartRowTotalPrice: { value: 0, currencyCode: baseCurrencyCode },
      cartRowTotalPriceInclRowDiscount: { value: 0, currencyCode: baseCurrencyCode },
      cartRowDiscountPercent: percentDiscount ?? customerPrice.customerDiscountPercent ?? 0,
      cartRowDiscountAmountPerUnit: {
        value: !isNaN(valueDiscount) ? valueDiscount : customerPrice.customerDiscountAmount.value,
        currencyCode: baseCurrencyCode
      },
      cartRowDiscountAmountTotal: { value: 0, currencyCode: baseCurrencyCode }
    };

    res.cartRowTotalPrice = {
      value: res.cartRowUnitPrice.value * res.quantity,
      currencyCode: res.cartRowUnitPrice.currencyCode
    };

    res.cartRowTotalPriceInclRowDiscount = {
      value: res.cartRowUnitPriceInclRowDiscount.value * res.quantity,
      currencyCode: res.cartRowTotalPriceInclRowDiscount.currencyCode
    };

    res.cartRowDiscountAmountTotal = {
      value: res.cartRowDiscountAmountPerUnit.value * res.quantity,
      currencyCode: res.cartRowDiscountAmountTotal.currencyCode
    };
    return res;
  }

  isStaffedPrice(price: CustomerProductPrice): boolean {
    return !!(price?.backendPrice?.levels?.length > 1);
  }

  getPriceListOptions(): Observable<Pricelist[]> {
    const url = '/json/pricelist/options';
    return this.http.get<Pricelist[]>(url);
  }

  getCartTotal(cart: CartRow[], checkout) {
    return this.getCartRowPricesObservable(cart); // This can be changed to use the OrderTotalService in the backend
  }
}
